import React from 'react';
import getConfig from 'next/config';
import {
  HouseIcon,
  HomePriceGuideIcon,
  AuctionResultsIcon,
} from '@domain-group/fe-co-icon';
import { urlUtil } from '@domain-group/fe-helper';
import {
  FAA_LANDING_PAGE_MODULE,
  entryPoint,
  trackAppraisalFunnelEntry,
} from '../../../../tracking/landing-page';
import type {
  FaaLandingPageModule,
  CtaDriverTrackingProps,
} from '../../../../tracking/landing-page';
import CtaItem from './components/cta-item';
import * as styles from './call-to-actions.style';

interface CallToActionsProps {
  onModuleRendered?: (module: FaaLandingPageModule) => void;
  modules?: FaaLandingPageModule[];
}

interface CtaConfig {
  module: FaaLandingPageModule;
  icon: React.ReactNode;
  href: string;
  label: [string, string];
  testId?: string;
  ctaDriver: CtaDriverTrackingProps;
  onAdditionalClick?: () => void;
}

const CallToActions = ({
  onModuleRendered,
  modules,
}: CallToActionsProps): JSX.Element => {
  const { publicRuntimeConfig } = getConfig();
  const baseUrlWithProtocol = publicRuntimeConfig?.BASE_URL || '';

  const ctaConfigs: CtaConfig[] = [
    {
      module: FAA_LANDING_PAGE_MODULE.CTA_DRIVER_GET_APPRAISAL,
      icon: <HouseIcon size="custom" />,
      href: urlUtil.createUrlWithParams({
        baseUrl: baseUrlWithProtocol,
        path: '/services/appraisal',
        params: {
          exitUrl: `${baseUrlWithProtocol}/real-estate-agents`,
          'entry-point': entryPoint,
        },
      }),
      label: ['Looking to sell?', 'Get a free appraisal'],
      testId: 'appraisal-link',
      ctaDriver: {
        interactionCTA: 'Looking to sell? Get a free appraisal',
        elementListPosition: 1,
        elementTargetCategoryPrimary: 'Request an Appraisal',
        elementTargetPageType: 'Funnel',
        elementTitle: 'Looking to sell?',
      },
      onAdditionalClick: trackAppraisalFunnelEntry,
    },
    {
      module: FAA_LANDING_PAGE_MODULE.CTA_DRIVER_TRACK_HOME,
      icon: <HomePriceGuideIcon size="custom" />,
      href: `${baseUrlWithProtocol}/owners`,
      label: ['Follow your property', 'to understand its value'],
      ctaDriver: {
        interactionCTA: 'Follow your property to understand its value',
        elementListPosition: 2,
        elementTargetCategoryPrimary: 'Owners',
        elementTargetPageType: 'Dashboard',
        elementTitle: 'Follow your property',
      },
    },
    {
      module: FAA_LANDING_PAGE_MODULE.CTA_DRIVER_RECENTLY_SOLD,
      icon: <AuctionResultsIcon size="custom" />,
      href: `${baseUrlWithProtocol}/?mode=sold`,
      label: ['See recently sold', 'properties in your area'],
      ctaDriver: {
        interactionCTA: 'See recently sold properties in your area',
        elementListPosition: 3,
        elementTargetCategoryPrimary: 'Sold',
        elementTargetPageType: 'Homepage',
        elementTitle: 'See recently sold',
      },
    },
  ];

  return (
    <ul css={styles.callToActions}>
      {ctaConfigs.map((config) => (
        <li key={config.module} css={styles.ctaItem}>
          <CtaItem
            module={config.module}
            icon={config.icon}
            href={config.href}
            styles={styles}
            testId={config.testId}
            ctaDriver={config.ctaDriver}
            onModuleRendered={onModuleRendered}
            onAdditionalClick={config.onAdditionalClick}
            modules={modules}
          >
            <div>
              <span css={styles.text}>{config.label[0]}</span>
              <span css={styles.mobileOnly}>&nbsp;</span>
              <span css={styles.text}>{config.label[1]}</span>
            </div>
          </CtaItem>
        </li>
      ))}
    </ul>
  );
};

export default CallToActions;
